import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, doc, deleteDoc, updateDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';

const Students = () => {
  const [students, setStudents] = useState([]);
  const [newStudent, setNewStudent] = useState({ name: '', pid: '', email: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [currentStudentId, setCurrentStudentId] = useState(null);
  const navigate = useNavigate();

  // Fetch all students
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoading(true);
        const q = query(collection(db, 'students'), orderBy('name'));
        const querySnapshot = await getDocs(q);
        const studentList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setStudents(studentList);
      } catch (err) {
        console.error('Error fetching students:', err);
        setError('Failed to load students. Please refresh and try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, []);

  // Add a new student
  const handleAddStudent = async (e) => {
    e.preventDefault();
    
    if (!newStudent.name.trim()) {
      setError('Student name is required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      if (editMode && currentStudentId) {
        // Update existing student
        await updateDoc(doc(db, 'students', currentStudentId), {
          name: newStudent.name,
          pid: newStudent.pid,
          email: newStudent.email,
          updatedAt: new Date().toISOString()
        });
        setSuccess('Student updated successfully!');
      } else {
        // Add new student
        await addDoc(collection(db, 'students'), {
          name: newStudent.name,
          pid: newStudent.pid,
          email: newStudent.email,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          checkedOutItems: []
        });
        setSuccess('Student added successfully!');
      }
      
      // Refresh students list
      const q = query(collection(db, 'students'), orderBy('name'));
      const querySnapshot = await getDocs(q);
      const studentList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setStudents(studentList);
      
      // Reset form
      setNewStudent({ name: '', pid: '', email: '' });
      setEditMode(false);
      setCurrentStudentId(null);
    } catch (err) {
      console.error('Error adding/updating student:', err);
      setError('Failed to save student. Please try again.');
    } finally {
      setLoading(false);
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  // Delete a student
  const handleDeleteStudent = async (id) => {
    if (!window.confirm('Are you sure you want to delete this student?')) {
      return;
    }
    
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'students', id));
      
      // Update state to remove deleted student
      setStudents(students.filter(student => student.id !== id));
      setSuccess('Student deleted successfully!');
    } catch (err) {
      console.error('Error deleting student:', err);
      setError('Failed to delete student. Please try again.');
    } finally {
      setLoading(false);
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  // Edit a student
  const handleEditStudent = (student) => {
    setNewStudent({
      name: student.name,
      pid: student.pid || '',
      email: student.email || ''
    });
    setEditMode(true);
    setCurrentStudentId(student.id);
  };

  return (
    <Layout>
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-6">Student Management</h1>
        
        {/* Add/Edit Student Form */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-8">
          <h2 className="text-xl font-semibold mb-4">{editMode ? 'Edit Student' : 'Add New Student'}</h2>
          
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          
          {success && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              {success}
            </div>
          )}
          
          <form onSubmit={handleAddStudent}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newStudent.name}
                  onChange={(e) => setNewStudent({...newStudent, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white text-black"
                  placeholder="Full Name"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2">
                  PID / Student ID
                </label>
                <input
                  type="text"
                  value={newStudent.pid}
                  onChange={(e) => setNewStudent({...newStudent, pid: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white text-black"
                  placeholder="Student ID"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={newStudent.email}
                  onChange={(e) => setNewStudent({...newStudent, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white text-black"
                  placeholder="student@ucf.edu"
                />
              </div>
            </div>
            
            <div className="flex justify-end mt-4">
              {editMode && (
                <button
                  type="button"
                  onClick={() => {
                    setNewStudent({ name: '', pid: '', email: '' });
                    setEditMode(false);
                    setCurrentStudentId(null);
                  }}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 mr-2"
                >
                  Cancel
                </button>
              )}
              
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Processing...' : editMode ? 'Update Student' : 'Add Student'}
              </button>
            </div>
          </form>
        </div>
        
        {/* Students List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <h2 className="text-xl font-semibold p-6 border-b">Students List</h2>
          
          {loading && students.length === 0 ? (
            <div className="p-6 text-center text-gray-500">Loading students...</div>
          ) : students.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No students found. Add your first student above.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items Checked Out</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {students.map((student) => (
                    <tr key={student.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{student.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                        {student.pid || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                        {student.email || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                        {student.checkedOutItems?.length || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEditStudent(student)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteStudent(student.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Students;
